import { Fragment, useContext, useEffect, useState } from 'react';
import { Breadcrumb, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import BaseHeader from '../../shared/Header';
import CPageCard from '../../shared/PageCard';
import {getAvatar, getObjectId} from '../../utils/helpers';
import {
  convertDurationToHrsAndMins,
  convertTimeTo12HourFormat,
  formatRatingScore,
  pluralize,
  weekMonthDayYear
} from '../../utils/stringManipulation';
import CButton from '../../shared/forms/Button';
import Rating from 'react-rating';
import starEmptyIcon from '../../assets/images/star-empty-icon.svg';
import starIcon from '../../assets/images/star-icon.svg';
import { AuthContext } from '../../auth/useAuthContext';
import {BookingContext} from './useBookingContext';
import { Constants } from '../../utils/constants';
import CConfirmationModal from '../../shared/forms/ConfirmationModal';
import CFormTextarea from '../../shared/forms/Textarea';
import Loader from '../../shared/Loader';
import {BookingAttendantDetailType, BookingAttributeType} from '../../utils/types/booking_types';
import CModal from '../../shared/forms/Modal';
import { RatingAttributeType } from '../../utils/types/util_types';

const BookingBreadCrumb = ({ attributes }: {attributes: BookingAttributeType}) => {
  const date = weekMonthDayYear(attributes.date, attributes.business_timezone);
  const formattedQueryStringDate = attributes.date;
  const status = () => {
    switch (attributes.status) {
      case Constants.Statuses.Booking.Unconfirmed:
        return 'unconfirmed';
      case Constants.Statuses.Booking.Completed:
      case Constants.Statuses.Booking.BusinessReimbursed:
        return 'finished';
      case Constants.Statuses.Booking.Canceled:
        return 'canceled';
      default:
        return '';
    }
  };

  const link = `/bookings?from=${formattedQueryStringDate}&to=${formattedQueryStringDate}&status=${status()}`;

  return (
    <Fragment>
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/bookings' }}>
          Bookings
        </Breadcrumb.Item>
        <Breadcrumb.Item
          className="capitalize"
          linkAs={Link}
          linkProps={{to: link}}
        >
          {date}
        </Breadcrumb.Item>
        <Breadcrumb.Item active className="capitalize">
          {`${attributes.service_name} - ${attributes.customer_name}`}
        </Breadcrumb.Item>
      </Breadcrumb>
    </Fragment>
  );
};

interface InformationProps {
  heading?: boolean;
  title: string;
  value: string | undefined;
}

const Information = ({ heading = false, title, value }: InformationProps) => {
  return <Fragment>
    <small>{title}</small>
    <p className={`text-dark ${heading ? '' : 'mb-3'}`}>{value}</p>
  </Fragment>;
};

const BookingDetails = () => {
  const [bookingAttributes, setBookingAttributes] = useState<BookingAttributeType>({} as BookingAttributeType);
  const [ratingAttributes, setRatingAttributes] = useState<RatingAttributeType>({} as RatingAttributeType);
  const [loading, setLoading] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [ratingId, setRatingId] = useState<number>();
  const [customerRating, setCustomerRating] = useState(0);
  const [businessRating, setBusinessRating] = useState(0);
  const [customerRatingNote, setCustomerRatingNote] = useState('');
  const [businessRatingNote, setBusinessRatingNote] = useState('');
  const [bookingId, setBookingId] = useState<number>();
  const [attendantDetails, setAttendantDetails] = useState<BookingAttendantDetailType>({} as BookingAttendantDetailType);
  const [ratingStatus, setRatingStatus] = useState('initialized');
  const [cancellationNote, setCancellationNote] = useState('');
  const [showCancellationNote, setShowCancellationNote] = useState(false);

  const { isAdmin, state: authState } = useContext(AuthContext);
  const { getBooking, rateCustomer, cancelBooking } = useContext(BookingContext);

  useEffect(() => {
    (async () => {
      const id = getObjectId();

      setBookingId(id as number);
      const result = await getBooking(id as number);

      if (result) {
        const attributes = result.attributes;
        const ratingForAttributes = attributes.rating?.attributes;

        setBookingAttributes(attributes);
        setRatingAttributes(ratingForAttributes);
        setRatingId(ratingForAttributes?.id);
        setCustomerRating(ratingForAttributes?.customer_score);
        setBusinessRating(ratingForAttributes?.entity_score);
        setCustomerRatingNote(ratingForAttributes?.customer_note);
        setBusinessRatingNote(ratingForAttributes?.entity_note);
        setRatingStatus(ratingForAttributes?.status);
        setAttendantDetails(attributes.attendant_details);
      }
    })();
  }, [refetch]);

  const timeSlot = (timezone: string) => {
    if (!bookingAttributes.date) return;

    const startTime = convertTimeTo12HourFormat(bookingAttributes.date, timezone);
    const finishTime = convertTimeTo12HourFormat(bookingAttributes.finish_time, timezone);

    return `${startTime} - ${finishTime}`;
  };

  const statusColor = () => {
    switch (bookingAttributes.status) {
      case Constants.Statuses.Booking.Unconfirmed:
        return 'color-amber';
      case Constants.Statuses.Booking.Completed:
      case Constants.Statuses.Booking.Confirmed:
      case Constants.Statuses.Booking.BusinessReimbursed:
        return 'color-primary';
      default:
        return 'color-error';
    }
  };

  const status = () => {
    switch (bookingAttributes.status) {        
      case Constants.Statuses.Booking.Completed:
      case Constants.Statuses.Booking.Confirmed:
      case Constants.Statuses.Booking.BusinessReimbursed:
        return Constants.Statuses.Booking.State.Paid;
      case Constants.Statuses.Booking.Canceled: {
        const transaction = bookingAttributes.transaction;

        if (transaction) {
          if (transaction.attributes.status == Constants.Statuses.Transaction.Refunded) {
            return Constants.Statuses.Booking.State.Refunded;
          } else {
            return Constants.Statuses.Booking.State.RefundPending;
          }
        } else {
          return Constants.Statuses.Booking.State.Canceled;
        }
      }
      default:
        return Constants.Statuses.Booking.State.Pending;
    }
  };

  const isFullyRated = ratingStatus == Constants.Statuses.Rating.Completed;
  const ratedByCustomer = ratingStatus == Constants.Statuses.Rating.CompletedByCustomer;
  const ratedByBusiness = ratingStatus == Constants.Statuses.Rating.CompletedByBusiness;
  const ratingPending = ratingStatus == Constants.Statuses.Rating.Pending;
  const ratingExpired = ratingStatus == Constants.Statuses.Rating.Expired;

  const canRateBooking =
    (ratingStatus == Constants.Statuses.Rating.Pending) ||
    (ratingStatus == Constants.Statuses.Rating.CompletedByCustomer);

  const isCanceled = bookingAttributes.status === Constants.Statuses.Booking.Canceled;

  const isAttendant =
    (authState.user.id == attendantDetails.id) && (
      (isAdmin() && (attendantDetails.actor == Constants.UserType.Admin)) ||
      (!isAdmin() && (attendantDetails.actor == Constants.UserType.Employee))
    );

  const showCancelButton = (isAttendant || isAdmin()) &&
    !isCanceled &&
    bookingAttributes.status !== Constants.Statuses.Booking.BusinessReimbursed &&
    bookingAttributes.status !== Constants.Statuses.Booking.Completed;

  const handleCancelBooking = async () => {
    setLoading(true);
    try {
      await cancelBooking(bookingId as number, cancellationNote.trim());
      setLoading(false);
      setShowCancelModal(false);
      setRefetch(!refetch);
    } catch (error) {
      setLoading(false);
      setShowCancelModal(false);
    }
  };

  const handleCancellationClose = () => {
    setCancellationNote('');
    setShowCancelModal(false);
  };

  const handleRating = async () => {
    if (!ratingAttributes) return;

    setLoading(true);
    try {
      await rateCustomer(ratingId as number, {
        score: customerRating,
        note: customerRatingNote
      });
      setLoading(false);
      setShowRatingModal(false);
      setRefetch(!refetch);
    } catch (error) {
      setLoading(false);
      setShowRatingModal(false);
    }
  };

  const handleRatingClose = () => {
    setCustomerRating(0);
    setShowRatingModal(false);
  };

  const ratingStarForm = (score = 0) => {
    return <Rating
      className="d-flex bookings__rating my-3"
      emptySymbol={<img alt="no star" src={starEmptyIcon} className="bookings__star" />}
      fullSymbol={<img alt="star" src={starIcon} className="bookings__star" />}
      placeholderRating={customerRating ?? formatRatingScore(score)}
      placeholderSymbol={<img alt="selected-star" src={starIcon} className="bookings__star" />}
      readonly={Number(score) > 0 && !isAttendant}
      onClick={(value: number) => setCustomerRating(value)}
    />;
  };

  const ratingForm = () => {
    return <Fragment>
      <small>Rate customers to help us improve the Corna experience</small>
      <p className="text-dark mt-3 mb-2">
        {`How was your experience with ${bookingAttributes.customer_first_name}?`}
      </p>

      <CFormTextarea value={customerRatingNote} onInput={setCustomerRatingNote} />
      {ratingStarForm()}

      <CButton
        medium
        disabled={!customerRating || customerRating === 0}
        onClick={() => setShowRatingModal(true)}
      >
        Submit rating
      </CButton>
    </Fragment>;
  };

  const ratingSection = () => {
    if (isFullyRated) {
      return <Fragment>
        {customerRatingNote.length > 0 && <p>{customerRatingNote}</p>}
        {ratingStarForm(customerRating)}
        
        <div className="mt-3">
          <h5>Business rating</h5>
          <div className="d-flex align-items-start small">
            <img src={starIcon} alt="customer's overall rating" className="analytics-customer-rating" />
            <span className="ps-1">{formatRatingScore(businessRating)}</span>
          </div>

          <p>{businessRatingNote.length > 0 && businessRatingNote}</p>
        </div>;
      </Fragment>;
    } else if (ratedByCustomer) {
      return <Fragment>
        {isAttendant ?
          <div>
            <p className="small mb-3">
              {bookingAttributes.customer_first_name} has rated you. Complete your rating to see theirs.
            </p>
            {ratingForm()}
          </div> :
          <p>{`There is no rating for ${bookingAttributes.customer_first_name} yet`}</p>
        }
      </Fragment>;
    } else if (ratedByBusiness) {
      return <Fragment>
        {customerRatingNote.length > 0 && <p>{customerRatingNote}</p>}
        {ratingStarForm(customerRating)}
      </Fragment>;
    } else if (ratingExpired) {
      return <p>This booking can no longer be rated</p>;
    } else if (ratingPending) {
      return isAttendant ? <Fragment>
        {ratingForm()}
      </Fragment> : <p>{`There is no rating for ${bookingAttributes.customer_first_name} yet`}</p>;
    } else if (isCanceled) {
      return <p>This booking has been canceled so it can no longer be rated</p>;
    } else {
      return isAttendant ? <p>
        {`After the service is performed, you will the able to rate ${bookingAttributes.customer_first_name}`}
      </p> : <p>{`There is no rating for ${bookingAttributes.customer_first_name} yet`}</p>;
    }
  };

  const avatar = getAvatar(authState.user.profile.attributes);

  return (
    <Fragment>
      <CModal
        show={showCancelModal}
        title="Cancel booking"
        onClose={handleCancellationClose}
        footer={
          <CButton
            loading={loading}
            onClick={handleCancelBooking}
          >
            Cancel booking
          </CButton>
        }
      >
        <>
          <CFormTextarea
            placeholder={`Tell ${bookingAttributes.customer_first_name} why their booking is being canceled`}
            value={cancellationNote}
            className="mt-2"
            onInput={setCancellationNote}
          />
          <p className="mt-3">{`${bookingAttributes.customer_first_name} will be notified and refunded.`}</p>
          {!isAdmin() && <p>{`${bookingAttributes.admin_first_name} will also be notified.`}</p>}
        </>
      </CModal>
      <CConfirmationModal
        show={showRatingModal}
        type="rating"
        cta="Submit"
        hasStandardNote={false}
        loading={loading}
        customNote={
          <Fragment>
            <p className="mb-2">You can not edit this rating.</p>
            <p>{`Are you sure you want to give ${bookingAttributes.customer_first_name} ${pluralize(customerRating, 'star')} for this booking?`}</p>
          </Fragment>
        }
        onClick={handleRating}
        onClose={handleRatingClose}
      />
      <CModal
        show={showCancellationNote}
        title="Cancellation note"
        onClose={() => setShowCancellationNote(false)}
        footer={
          <div>
            <CButton loading={loading as boolean} small onClick={() => setShowCancellationNote(false)}>
              Ok
            </CButton>
          </div>
        }
      >
        {bookingAttributes.cancellation_note}
      </CModal>

      {Object.keys(bookingAttributes).length === 0 ? <Loader /> :
        <Fragment>
          <BaseHeader header={<BookingBreadCrumb attributes={bookingAttributes} />} />

          <CPageCard>
            <CPageCard.Title>
              <h5>Customer Information</h5>
              <div className="c-sidebar__user p-0 d-flex">
                <div className="col-1 px-0">
                  <div className="d-flex">
                    <img src={avatar} alt="user avatar" className="bookings__img" />
                  </div>
                </div>

                <div className="col-11 col-lg-10 px-0">
                  <div className="d-flex">
                    <div className="col-4">
                      <Information
                        heading={true}
                        title="Customer name"
                        value={bookingAttributes.customer_name}/>
                    </div>
                    <div className="col-4 px-0">
                      <Information
                        heading={true}
                        title="Phone number"
                        value={bookingAttributes.customer_contact_number}
                      />
                    </div>
                    <div className="col-4 px-0">
                      <Information
                        heading={true}
                        title="Email address"
                        value={bookingAttributes.customer_email}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CPageCard.Title>
            <CPageCard.Content>
              <Row>
                <Col lg={3} xl={3}>
                  <div>
                    <h5>Service Information</h5>
                    <Information title="Service" value={bookingAttributes.service_name} />
                    <Information
                      title="Price"
                      value={bookingAttributes.service_amount}
                    />
                    <div className="d-flex">
                      <div className="pe-lg-3 pe-4">
                        <Information
                          title="Service Duration"
                          value={convertDurationToHrsAndMins(bookingAttributes.service_duration)}
                        />
                      </div>
                      <div>
                        <Information title="Service Category" value={bookingAttributes.category_name} />
                      </div>
                    </div>
                    <Information
                      title="Service assigned to"
                      value={bookingAttributes.attendant_details?.name}
                    />
                  </div>
                </Col>
                <Col lg={1} className="profile__form-vr" />
                <Col lg={4} xl={4}>
                  <div>
                    <h5>Booking Information</h5>
                    <Information
                      title="Date"
                      value={weekMonthDayYear(bookingAttributes.date, bookingAttributes.business_timezone)}
                    />
                    <Information title="Time" value={timeSlot(bookingAttributes.business_timezone)} />
                    {bookingAttributes.note && <Information
                      title="Note"
                      value={bookingAttributes.note}
                    />}
                    <small>Payment Status</small>
                    <p className="mb-5">
                      <strong className={statusColor()}>{status()}</strong>
                      {status() === 'Canceled' && bookingAttributes.cancellation_note &&
                        <span
                          className="text-dark small bookings__cancellation-note-link"
                          onClick={() => setShowCancellationNote(true)}
                        > — View note
                        </span>
                      }
                    </p>

                    <br />
                    {showCancelButton &&
                      <CButton
                        color="regular"
                        medium
                        onClick={() => setShowCancelModal(true)}
                      >
                        Cancel Booking
                      </CButton>}
                  </div>
                </Col>
                <Col lg={1} className="profile__form-vr" />
                <Col lg={3} xl={3}>
                  <div>
                    <h5>{`${isAttendant && canRateBooking ? 'Rate this customer' : 'Customer rating'}`}</h5>
                    {ratingSection()}
                  </div>
                </Col>
              </Row>
            </CPageCard.Content>
          </CPageCard>
        </Fragment>
      }
    </Fragment>
  );
};

export default BookingDetails;
